import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

import App from 'App';

import { PERIOD_ALL_TIME, PERIOD_CUSTOM } from '../time_report/timeReportFormat';

import TimeOverviewFilters, { STATUS_ALL } from './TimeOverviewFilters';
import { COLUMN_DEFINITIONS, hiddenNote } from './timeOverviewFormat';
import TimeOverviewKpis from './TimeOverviewKpis';
import TimeOverviewTable from './TimeOverviewTable';

import Alert from 'CommonComponents/Alert';
import PaginationInfo from 'CommonComponents/PaginationInfo';
import TablePagination from 'CommonComponents/TablePagination';

const EMPTY_ROWS = [];

const SORT_NAME = 'name';

/**
 * The filters and sort as query parameters. The export link and the data
 * endpoint read the same object, so a sheet can never reflect a different
 * period, status, toggle or order from the table it was downloaded from.
 */
function queryFor(filters, sort, page) {
    const params = { period: filters.period, status: filters.status, sort: sort.key, direction: sort.direction, page };

    if (filters.showEmpty) {
        params.show_empty = 1;
    }

    if (filters.period === PERIOD_CUSTOM) {
        if (filters.from) {
            params.from = filters.from;
        }

        if (filters.to) {
            params.to = filters.to;
        }
    }

    return params;
}

/** The same one-line definitions the column and card (i) buttons show. */
function TimeOverviewDefinitions() {
    return (
        <section aria-labelledby="time_overview_definitions" className="tw-pt-4 tw-border-t-2 tw-border-grey">
            <h4 id="time_overview_definitions" className="tw-text-base tw-font-semibold tw-text-charcoal tw-mb-3">
                How these figures are calculated
            </h4>
            <dl className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 xl:tw-grid-cols-4 tw-gap-x-5 tw-gap-y-3 tw-mb-0">
                {Object.entries(COLUMN_DEFINITIONS).map(([key, { label, definition }]) => (
                    <div key={key}>
                        <dt className="tw-text-sm tw-font-medium tw-text-charcoal">{label}</dt>
                        <dd className="tw-text-sm tw-text-charcoal-60 tw-mb-0">{definition}</dd>
                    </div>
                ))}
            </dl>
        </section>
    );
}

export default function ProjectsTimeOverviewIndex({ dataUrl, exportUrl, currency }) {
    const [filters, setFilters] = useState({
        period: PERIOD_ALL_TIME,
        status: STATUS_ALL,
        from: '',
        to: '',
        showEmpty: false,
    });
    const [sort, setSort] = useState({ key: SORT_NAME, direction: 'asc' });
    const [page, setPage] = useState(1);

    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const readSeq = useRef(0);

    const fetchReport = useCallback(() => {
        const sequence = ++readSeq.current;

        setLoading(true);
        setError(null);

        return axios
            .get(dataUrl, { params: queryFor(filters, sort, page) })
            .then(({ data }) => {
                // Only the newest read may land. The controls stay live while
                // one is in flight, so an earlier response arriving last would
                // put a table on screen that no longer matches them.
                if (sequence === readSeq.current) {
                    setReport(data.data);
                }
            })
            .catch(() => {
                if (sequence === readSeq.current) {
                    setError($t('Something went wrong'));
                }
            })
            .finally(() => {
                if (sequence === readSeq.current) {
                    setLoading(false);
                }
            });
    }, [dataUrl, filters, sort, page]);

    useEffect(() => {
        fetchReport();
    }, [fetchReport]);

    const changeFilters = useCallback((change) => {
        // Back to the first page: the page number belongs to the project list
        // the old period produced, and page 2 of a shorter list is empty.
        setPage(1);
        setFilters((previous) => ({ ...previous, ...change }));
    }, []);

    const changeSort = useCallback((key) => {
        // A sort re-ranks every project, so the old page number points into a
        // different list.
        setPage(1);
        setSort((previous) => ({
            key,
            direction: previous.key === key && previous.direction === 'asc' ? 'desc' : 'asc',
        }));
    }, []);

    const exportReport = useCallback(() => {
        const params = new URLSearchParams(queryFor(filters, sort, page));

        window.location.assign(`${exportUrl}?${params.toString()}`);
    }, [exportUrl, filters, sort, page]);

    const pagination = report?.pagination;

    return (
        <div className="tw-space-y-4">
            {error && <Alert type="error">{error}</Alert>}

            <TimeOverviewKpis total={report?.total} />

            <div className="table__card tw-space-y-4">
                <div>
                    <h3 className="table__header tw-mb-1">{$t('All projects')}</h3>
                    <p className="tw-text-sm tw-text-charcoal-50 tw-mb-0">
                        For each project: what has been budgeted to the client for time, what that time costs, and how the margin is tracking against budget.
                        {currency?.code && ` All values in ${currency.code}.`}
                    </p>
                </div>

                {/* The controls stay live while a read is in flight — changing
                    your mind mid-request should not need the request to finish.
                    fetchReport()'s sequence guard is what keeps the table
                    matching them. */}
                <TimeOverviewFilters filters={filters} onChange={changeFilters} onExport={exportReport} />

                {loading && !report && <p className="tw-text-sm tw-text-charcoal-75 tw-mb-0">{$t('Loading')}…</p>}

                {report && (
                    <TimeOverviewTable
                        rows={report.rows ?? EMPTY_ROWS}
                        total={report.total}
                        hiddenCount={report.hidden_count}
                        sort={sort.key}
                        direction={sort.direction}
                        onSort={changeSort}
                    />
                )}

                {pagination && pagination.last_page > 1 && (
                    <div className="table__pagination-info">
                        <PaginationInfo from={pagination.from} to={pagination.to} total={pagination.total} />
                        <TablePagination lastPage={pagination.last_page} pageChange={(data) => setPage(+data.selected + 1)} currentPage={pagination.page} />
                    </div>
                )}

                {report?.hidden_count > 0 && <p className="tw-text-sm tw-text-charcoal-50 tw-mb-0">{hiddenNote(report.hidden_count)}</p>}

                <TimeOverviewDefinitions />
            </div>
        </div>
    );
}

if (document.getElementById('ProjectsTimeOverview')) {
    const rootEl = document.getElementById('ProjectsTimeOverview');

    ReactDOM.render(
        <App>
            <ProjectsTimeOverviewIndex
                dataUrl={rootEl.getAttribute('data-data-url')}
                exportUrl={rootEl.getAttribute('data-export-url')}
                currency={JSON.parse(rootEl.getAttribute('data-currency') || 'null')}
            />
        </App>,
        rootEl,
    );
}
